import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, Link, useSearchParams, useLocation } from "react-router-dom";
import { sendOtp, verifyOtp } from "../api/auth.js";
import OtpInputs from "../components/OtpInputs.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { isValidIndianPhoneInput, sanitizePhoneInputDigits } from "../utils/phone.js";

export default function LoginPage() {
  const nav = useNavigate();
  const [search] = useSearchParams();
  const { state } = useLocation();
  const next = search.get("next") || "/";

  const { login } = useAuth();

  const [phone, setPhone] = useState("");
  const [step, setStep] = useState("phone"); // phone | otp
  const [request, setRequest] = useState(null); // {requestId, phone}
  const [otp, setOtp] = useState("");
  const [timer, setTimer] = useState(0);
  const [loading, setLoading] = useState(false);
  const validPhone = isValidIndianPhoneInput(phone);

  useEffect(() => {
    if (timer <= 0) return;
    const t = setTimeout(() => setTimer((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [timer]);

  const canSubmitOtp = useMemo(() => otp.length === 6, [otp]);

  const onSend = async () => {
    try {
      setLoading(true);
      const res = await sendOtp(phone);
      // ensure phone stored even if API doesn't echo it
      setRequest({ ...(res || {}), phone });
      setStep("otp");
      setTimer(30);
    } catch (e) {
      alert(e.message || "Failed to send OTP");
    } finally {
      setLoading(false);
    }
  };

  // inside LoginPage.jsx
 // inside LoginPage.jsx
const onVerify = async () => {
  try {
    setLoading(true);
    const resp = await verifyOtp({ mobile: phone, code: otp }); // <-- +91 add auth.js me ho raha
    const token = resp?.token 
    const userResp = resp.data 
    const finalUser = {
      id: userResp?._id ?? phone,
      name: userResp?.name ?? "Guest",
      phone,
      mobile: userResp?.mobile || phone,
      email: userResp?.email || "",
      role: userResp?.role,
    };
    login(finalUser, token);
    nav(next, { replace: true });
  } catch (e) {
    alert(e.message || "OTP verification failed");
  } finally {
    setLoading(false);
  }
};

  const onResend = async () => {
    if (timer > 0) return;
    await sendOtp(phone);
    setStep("enterOtp");
  };

  return (
    <>
      {/* top full-bleed yellow */}
      <section className="w-full bg-[#fff8e1] border-b">
        <div className="mx-auto max-w-7xl px-4 md:px-6 py-5">
          <h1 className="text-xl font-extrabold text-slate-900">Login</h1>
          <p className="text-sm text-slate-700">Sign in with your mobile number using OTP.</p>
        </div>
      </section>

      <main className="w-full">
        <div className="mx-auto max-w-md px-4 md:px-6 py-6">
          <div className="rounded-2xl border bg-white shadow-sm p-5">
            {step === "phone" ? (
              <>
                <label className="text-sm text-slate-600">Mobile Number</label>
                <div className="mt-1 flex">
                  <span className="inline-flex items-center rounded-l-lg border border-r-0 px-3 text-slate-600 bg-slate-50">
                    +91
                  </span>
                  <input
                    className="flex-1 rounded-r-lg border px-3 py-2 outline-none focus:ring-2 focus:ring-amber-300"
                    placeholder="10-digit mobile"
                    value={phone}
                    onChange={(e) => setPhone(sanitizePhoneInputDigits(e.target.value))}
                  />
                </div>
                <button
                  disabled={!validPhone || loading}
                  onClick={onSend}
                  className={`mt-4 w-full rounded-full px-5 py-2.5 font-semibold ${validPhone && !loading
                      ? "bg-amber-400 text-slate-900 hover:bg-amber-300"
                      : "bg-slate-200 text-slate-500 cursor-not-allowed"
                    }`}
                >
                  {loading ? "Sending..." : "Send OTP"}
                </button>

                <p className="mt-4 text-sm text-slate-600">
                  New here?{" "}
                  <Link to="/register" className="text-amber-700 font-semibold hover:underline">
                    Create an account
                  </Link>
                </p>
              </>
            ) : (
              <>
                <div className="text-sm text-slate-700">
                  OTP sent to <span className="font-semibold">{phone}</span>
                </div>

                <div className="mt-3">
                  <OtpInputs value={otp} onChange={setOtp} />
                </div>

                <div className="mt-3 flex items-center justify-between">
                  <button
                    onClick={() => setStep("phone")}
                    className="text-sm text-slate-600 hover:underline"
                  >
                    Change number
                  </button>
                  <button
                    onClick={onResend}
                    disabled={timer > 0 || loading}
                    className={`text-sm font-semibold ${timer > 0 ? "text-slate-400" : "text-amber-700 hover:underline"
                      }`}
                  >
                    {timer > 0 ? `Resend in 0:${String(timer).padStart(2, "0")}` : "Resend OTP"}
                  </button>
                </div>

                <button
                  disabled={!canSubmitOtp || loading}
                  onClick={onVerify}
                  className={`mt-4 w-full rounded-full px-5 py-2.5 font-semibold ${canSubmitOtp && !loading
                      ? "bg-amber-400 text-slate-900 hover:bg-amber-300"
                      : "bg-slate-200 text-slate-500 cursor-not-allowed"
                    }`}
                >
                  {loading ? "Verifying..." : "Verify & Login"}
                </button>
              </>
            )}
          </div>
        </div>
      </main>
    </>
  );
}
